import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import { Avatar } from "@design-system/Avatar/Avatar";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import type { DataGridPersonValue } from "./cellValueTypes";
import { EmptyCellValue } from "./EmptyCellValue";
import {
    isPersonValue,
    KICK_PERSON,
    toPersonDisplay,
} from "./listDisplayValues";
import { SearchableText } from "./SearchableText";
import type { DataGridCellProps } from "./types";
import content from "./cellContent.module.scss";
import styles from "./cells.module.scss";

interface PersonAvatarProps {
    person: DataGridPersonValue;
    /** The avatar's accessible name and the source of its initial. */
    name: string;
    isDecorative: boolean;
}

/**
 * `UserAvatar`'s look (outlined, colourless unless `iconColor` is set), or
 * `KickAvatar`'s for `KICK_PERSON`.
 */
export const PersonAvatar = ({
    person,
    name,
    isDecorative,
}: Readonly<PersonAvatarProps>) => {
    if (person === KICK_PERSON) {
        return (
            <Avatar
                name={name}
                fallback={<KickLogo variant="icon" shape="circle" />}
                size="xs"
                aria-hidden={isDecorative}
            />
        );
    }
    return (
        <Avatar
            name={name}
            src={person.avatarUrl}
            variant="outline"
            {...(person.iconColor === undefined
                ? { tone: "neutral" }
                : { tone: "colorful", color: person.iconColor })}
            size="xs"
            aria-hidden={isDecorative}
        />
    );
};

/** What the shared cell tooltip says for a person: why they're dimmed, if anything. */
export const toPersonTooltip = (person: DataGridPersonValue) =>
    person.tooltip ?? (person.isDimmed ? person.name : undefined);

interface PersonCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    fallback: "kick" | undefined;
}

/**
 * Person column cell: the avatar and name, or Kick for `null` with
 * `fallback: "kick"`. A dimmed person fades, and their `tooltip` shows on
 * hover and keyboard focus through the table's `CellTooltipHost`.
 */
export const PersonCell = <TData extends RowData>({
    context,
    meta,
    fallback,
}: Readonly<PersonCellProps<TData>>) => {
    const value = context.getValue();
    const person = toPersonDisplay(
        isPersonValue(value) ? value : null,
        fallback,
    );
    if (person === null) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const tooltip = toPersonTooltip(person);
    return (
        <span
            className={classNames(content.inline, styles.person)}
            data-dimmed={person.isDimmed ? "" : undefined}
            data-cell-tooltip={tooltip}
            data-cell-tooltip-always={tooltip === undefined ? undefined : ""}
        >
            <PersonAvatar person={person} name={person.name} isDecorative />
            <SearchableText
                text={person.name}
                overflow={meta.overflow}
                table={context.table}
            />
        </span>
    );
};
